import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Eye, EyeSlash, Translate } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

const DEFAULT_BG = "https://images.unsplash.com/photo-1742844551905-e07706ff2cc2?auto=format&fit=crop&w=1600&q=80";
const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { login } = useAuth();
  const { lang, setLang, t } = useI18n();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bg, setBg] = useState(DEFAULT_BG);
  const [tagline, setTagline] = useState({ eyebrow: "Colossae Hotel", title: "Personel Yönetim Sistemi", subtitle: "Ekip yönetimi, izinler, bordro ve iç iletişim tek panelde.", eyebrow_en: "Colossae Hotel", title_en: "Personnel Management System", subtitle_en: "Team management, leaves, payroll and internal messaging in one panel." });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/settings/login-image").then((r) => {
      const url = r.data.url;
      setBg(url.startsWith("http") ? url : `${API}${url}`);
    }).catch(() => {});
    api.get("/settings/login-tagline").then((r) => setTagline(r.data)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(identifier, password, rememberMe);
    setLoading(false);
    if (!res.ok) return toast.error(res.error);
    toast.success("Giriş başarılı");
    nav("/");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src={bg} className="absolute inset-0 w-full h-full object-cover" alt="hotel" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/80 to-primary/20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.3em] opacity-80">{(lang === "en" || lang === "id") ? (tagline.eyebrow_en || tagline.eyebrow) : tagline.eyebrow}</div>
          <div className="font-display text-4xl lg:text-5xl font-bold mt-3">{(lang === "en" || lang === "id") ? (tagline.title_en || tagline.title) : tagline.title}</div>
          <div className="mt-3 text-white/80 max-w-md">{(lang === "en" || lang === "id") ? (tagline.subtitle_en || tagline.subtitle) : tagline.subtitle}</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 bg-background relative">
        <div className="absolute top-4 right-4 flex items-center gap-1 bg-card border border-border rounded-full p-1" data-testid="lang-switcher">
          <Translate size={16} className="ml-2 text-muted-foreground" />
          <Button size="sm" variant={lang === "tr" ? "default" : "ghost"} className="rounded-full h-7 px-3 text-xs" onClick={() => setLang("tr")} data-testid="login-lang-tr">TR</Button>
          <Button size="sm" variant={lang === "en" ? "default" : "ghost"} className="rounded-full h-7 px-3 text-xs" onClick={() => setLang("en")} data-testid="login-lang-en">EN</Button>
          <Button size="sm" variant={lang === "id" ? "default" : "ghost"} className="rounded-full h-7 px-3 text-xs" onClick={() => setLang("id")} data-testid="login-lang-id">ID</Button>
        </div>
        <Card className="w-full max-w-md border-border">
          <CardContent className="p-8 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("app.name")}</div>
              <h1 className="font-display text-3xl font-bold mt-1">{t("login.title")}</h1>
              <div className="text-sm text-muted-foreground mt-1">{t("login.subtitle")}</div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label>{t("login.identifier")}</Label>
                <Input
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t("login.identifier_ph")}
                  data-testid="login-identifier"
                />
              </div>
              <div>
                <Label>{t("login.password")}</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="remember-me" checked={rememberMe} onCheckedChange={setRememberMe} data-testid="remember-me" />
                <Label htmlFor="remember-me" className="cursor-pointer text-sm">{t("login.remember")}</Label>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                {loading ? t("login.submitting") : t("login.submit")}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <Link to="/forgot-password" className="text-primary underline" data-testid="link-forgot">{t("login.forgot")}</Link>
                <Link to="/register" className="text-primary underline" data-testid="link-register">{t("login.register")}</Link>
              </div>
            </form>
          </CardContent>
        </Card>
        <div className="mt-6 flex justify-center gap-4 text-[11px] text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground underline decoration-dotted" data-testid="login-privacy">{t("login.privacy")}</Link>
          <Link to="/terms" className="hover:text-foreground underline decoration-dotted" data-testid="login-terms">{t("login.terms")}</Link>
        </div>
      </div>
    </div>
  );
}
